"use client";

/**
 * AdminInput — 라벨 + 입력 컨트롤 (경량 버전)
 *
 * AdminDescriptions layout="stacked" 안에서 편집 가능한 필드로 쓰거나,
 * 단독으로 간단한 입력이 필요할 때 사용한다.
 *
 * 복잡한 유효성 검사나 포맷 처리가 필요하면 AdminFormField 를 사용한다.
 *
 * layout 옵션:
 * - "stacked"(기본): 라벨이 input 위에 작게(xs, muted) 표시
 * - "inline":        라벨이 input 왼쪽에 한 줄로 표시
 *
 * size 옵션: "sm" | "default" | "lg"
 *
 * 추가 기능:
 * - type="password" 일 때 비밀번호 표시/숨김 토글 자동 제공
 * - clearable: 값이 있을 때 X 버튼으로 초기화
 *
 * @example
 * <AdminInput layout="stacked" label="아이디" value={id} onChange={setId} />
 * <AdminInput layout="stacked" label="비밀번호" type="password" value={pw} onChange={setPw} />
 * <AdminInput layout="stacked" label="검색" clearable value={q} onChange={setQ} />
 */
import { type ComponentProps, type ReactNode, useId, useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Label } from "../ui/label";

export type AdminInputSize = "sm" | "default" | "lg";

const SIZE_CLASS: Record<AdminInputSize, string> = {
  sm:      "h-8 text-xs",
  default: "h-10 text-sm",
  lg:      "h-12 text-base",
};

const ICON_SIZE: Record<AdminInputSize, number> = {
  sm:      13,
  default: 15,
  lg:      20,
};

// 버튼 자체 padding
const BTN_PAD: Record<AdminInputSize, string> = {
  sm:      "p-0.5",
  default: "p-1",
  lg:      "p-1.5",
};

// input 오른쪽 여백 (버튼 수 × 크기)
const RIGHT_PAD: Record<AdminInputSize, { none: string; one: string; two: string }> = {
  sm:      { none: "pr-3", one: "pr-8",  two: "pr-14" },
  default: { none: "pr-3", one: "pr-9",  two: "pr-16" },
  lg:      { none: "pr-4", one: "pr-12", two: "pr-20" },
};

export interface AdminInputProps
  extends Omit<ComponentProps<"input">, "onChange" | "size"> {
  /**
   * 레이아웃 방식
   * - "stacked"(기본): 라벨 위, input 아래
   * - "inline": 라벨 왼쪽, input 오른쪽
   */
  layout?: "stacked" | "inline";
  /** 입력 크기: "sm" | "default"(기본) | "lg" */
  size?: AdminInputSize;
  /** 필드 라벨 */
  label?: ReactNode;
  /** inline 일 때 라벨 너비 클래스 (기본 w-24) */
  labelClassName?: string;
  /** 입력값 변경 콜백 */
  onChange?: (value: string) => void;
  /** 값이 있을 때 X 버튼으로 초기화 */
  clearable?: boolean;
  /** 입력 아래 도움말 / 에러 메시지 */
  hint?: ReactNode;
  /** hint 를 에러(빨간색)로 표시 */
  error?: boolean;
  className?: string;
  /**
   * useAdminForm().field() 의 반환값을 그대로 스프레드해서 쓸 수 있도록 받아두는 값.
   * AdminInput 은 검증 UI를 그리지 않으므로 실제로는 사용하지 않는다 (필요하면 AdminFormField 사용).
   */
  validate?: (value: string) => string | undefined;
  forceValidate?: boolean;
  /** useAdminForm().field() 반환값 스프레드용 — AdminInput 은 자동 포커싱을 지원하지 않으므로 사용하지 않는다. */
  autoFocusOnError?: boolean;
  /** useAdminForm().field() 반환값 스프레드용 — 위와 동일한 이유로 사용하지 않는다. */
  focusToken?: number;
}

export function AdminInput({
  layout = "stacked",
  size = "default",
  label,
  labelClassName,
  onChange,
  clearable,
  hint,
  error,
  className,
  id: idProp,
  type,
  value,
  validate: _validate,
  forceValidate: _forceValidate,
  autoFocusOnError: _autoFocusOnError,
  focusToken: _focusToken,
  ...inputProps
}: AdminInputProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const isPassword = type === "password";
  const [showPw, setShowPw] = useState(false);
  const iconSize = ICON_SIZE[size];

  const hasRight = clearable || isPassword;
  const clearActive = clearable && value !== "" && value != null;
  const btnCount = (clearable ? 1 : 0) + (isPassword ? 1 : 0);
  const rightPadding = RIGHT_PAD[size][btnCount === 2 ? "two" : hasRight ? "one" : "none"];

  const inputEl = (
    <div className="relative">
      <input
        id={id}
        type={isPassword ? (showPw ? "text" : "password") : type}
        value={value}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange?.(e.target.value)}
        className={cn(
          "flex w-full min-w-0 appearance-none rounded-md border border-input bg-transparent pl-3 shadow-xs transition-colors outline-none",
          "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
          SIZE_CLASS[size],
          rightPadding
        )}
        {...inputProps}
      />

      {/* 오른쪽 버튼 영역 */}
      {hasRight && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-1">
          {/* Clear 버튼 */}
          {clearable && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => onChange?.("")}
              className={cn(
                "flex items-center justify-center rounded text-muted-foreground transition-opacity hover:text-foreground",
                BTN_PAD[size],
                clearActive ? "opacity-100" : "pointer-events-none opacity-0"
              )}
              aria-label="입력 초기화"
            >
              <X size={iconSize} />
            </button>
          )}

          {/* Password 토글 버튼 */}
          {isPassword && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPw((v) => !v)}
              className={cn("flex items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground", BTN_PAD[size])}
              aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 보기"}
            >
              {showPw ? <EyeOff size={iconSize} /> : <Eye size={iconSize} />}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const hintEl = hint ? (
    <p className={cn("mt-1 text-xs", error ? "text-destructive" : "text-muted-foreground")}>
      {hint}
    </p>
  ) : null;

  if (!label) {
    return (
      <div className={className}>
        {inputEl}
        {hintEl}
      </div>
    );
  }

  if (layout === "inline") {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        <Label
          htmlFor={id}
          className={cn("shrink-0 text-sm text-muted-foreground", labelClassName ?? "w-24")}
        >
          {label}
        </Label>
        <div className="min-w-0 flex-1">
          {inputEl}
          {hintEl}
        </div>
      </div>
    );
  }

  // stacked
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {inputEl}
      {hintEl}
    </div>
  );
}
